import { TFunction } from 'i18next';
import memoizeOne from 'memoize-one';
import { DeepReadonlyObject } from 'rxdb';

import { ItineraryView, TripTypeEnum, VehicleType } from '@mopla/data-models';
import { ERideType } from '@mopla/utils';

export const getPRODTLeg = memoizeOne(
	(itinerary?: DeepReadonlyObject<ItineraryView> | null) => {
		return (
			itinerary?.legs.find((leg) => leg.mode === TripTypeEnum.PRODT) ?? null
		);
	}
);

export function getLbodtflexLabel(
	itinerary: DeepReadonlyObject<ItineraryView> | null
): string | undefined {
	if (!itinerary) return undefined;
	return itinerary.legs.find((leg) => leg.mode === TripTypeEnum.LBODTFLEX)
		?.lbodtflexLabel;
}

export function getRideTypeTitle(
	rideType: ERideType,
	t: TFunction,
	vehicleType?: VehicleType,
	lbodtflexLabel?: string
): string {
	if (rideType === ERideType.PRODT && vehicleType) {
		return t(`booking:travel_types.${vehicleType}.title`);
	}
	if (rideType === ERideType.LBODTFLEX) {
		return lbodtflexLabel
			? t(`booking:travel_types.LBODTFLEX.title_flex`, { lbodtflexLabel })
			: t(`booking:travel_types.LBODTFLEX.title_default`);
	}
	return t(`booking:travel_types.${rideType}.title`);
}

/** Transport icon mode per ride type; both mixed types share one icon. */
const RIDE_TYPE_ICON_MODE: Record<ERideType, TripTypeEnum | 'MIXED'> = {
	[ERideType.PRODT]: TripTypeEnum.PRODT,
	[ERideType.LBODTFLEX]: TripTypeEnum.LBODTFLEX,
	[ERideType.LBODT]: TripTypeEnum.LBODT,
	[ERideType.LBT]: TripTypeEnum.LBT,
	[ERideType.SPECIAL_SCHOOL_TRAFFIC]: TripTypeEnum.SPECIAL_SCHOOL_TRAFFIC,
	[ERideType.MIXED]: 'MIXED',
	[ERideType.MIXED_PUBLIC]: 'MIXED',
};

export function getRideTypeIconMode(
	rideType: ERideType
): TripTypeEnum | 'MIXED' {
	return RIDE_TYPE_ICON_MODE[rideType];
}

export function getFullName(person: {
	firstName?: string | null;
	lastName?: string | null;
}): string {
	return [person.firstName, person.lastName].filter(Boolean).join(' ');
}

/** Returns the display title for a specific LBODTFLEX leg, falling back to the default title. */
export function getLbodtflexLegTitle(
	itinerary: DeepReadonlyObject<ItineraryView> | null,
	legId: string | undefined,
	t: TFunction
): string {
	const lbodtflexLabel = itinerary?.legs.find(
		(l) => l.legId === legId
	)?.lbodtflexLabel;
	return lbodtflexLabel ?? t('booking:travel_types.LBODTFLEX.title_default');
}
